/* ──────────────────────────────────────────────
   Blooms Savings Calculator
   Vanilla JS, React-style state + render
   ────────────────────────────────────────────── */

(function () {
  'use strict';

  const ROOT_ID = 'blooms-savings-calculator';

  const COMPANY_LABELS = {
    horeca:        'Horeca',
    kantoor_klein: 'Kantoor klein',
    kantoor_groot: 'Kantoor groot',
    hotel:         'Hotel',
    hospitality:   'Hospitality',
    winkel:        'Retail / winkel',
  };

  const LIMITS = {
    xl:     { min: 0,  max: 20,  step: 1 },
    m:      { min: 0,  max: 20,  step: 1 },
    plants: { min: 0,  max: 200, step: 1 },
    hourly: { min: 20, max: 120, step: 1 },
  };

  /* Verversingsfrequentie — hoe vaak verse boeketten vervangen worden.
     Multiplier = aantal swaps in 12 weken ÷ 12.                     */
  const FREQ = {
    weekly:   { label: 'Wekelijks',      mult: 1     }, // 12 swaps / 12 weken
    biweekly: { label: 'Om de 2 weken',  mult: 0.5   }, // 6 swaps
    monthly:  { label: 'Maandelijks',    mult: 0.25  }, // 3 swaps
  };

  /* ── Utils ── */
  const fmtEUR = (n) =>
    '€' + Math.round(n).toLocaleString('nl-NL', { maximumFractionDigits: 0 });

  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }

  /* Animate a numeric element from previous value to target */
  function animateNumber(el, to, format) {
    const from = Number(el.dataset.value || 0);
    if (from === to) {
      el.textContent = format(to);
      return;
    }
    el.dataset.value = to;
    const duration = 520;
    const start = performance.now();
    cancelAnimationFrame(el._raf || 0);
    function step(now) {
      const t = Math.min(1, (now - start) / duration);
      const val = from + (to - from) * easeOutCubic(t);
      el.textContent = format(val);
      if (t < 1) el._raf = requestAnimationFrame(step);
    }
    el._raf = requestAnimationFrame(step);
  }

  /* ── Calculation engine ──
     Bouquets: recurring — fresh weekly vs Blooms subscription (per month)
     Plants:   fresh is recurring, Blooms is a ONE-TIME purchase
     The 12-week savings compares recurring costs only (apples-to-apples);
     the plant investment is shown separately with payback time.           */
  function calculate(state, pricing) {
    const weeks = pricing.weeks;
    const months = weeks / 4.333; // ~2.77 months for 12 weeks

    const bouquetCount = state.xl + state.m;
    const hoursPerWeek =
      (bouquetCount * pricing.minutesPerBouquet + state.plants * pricing.minutesPerPlant) / 60;

    /* ─ Frequency: scales bouquet PRODUCT cost and HANDLING swaps ─
       Maintenance time stays constant (bouquets need care regardless
       of how often they're replaced). Plant costs are unaffected.  */
    const freqMult = (FREQ[state.freq] || FREQ.weekly).mult;
    const swaps = weeks * freqMult; // 12, 6, or 3

    /* ─ Fresh: recurring everything ─ */
    const freshBouquetProduct =
      (state.xl * pricing.freshXLPerWeek + state.m * pricing.freshMPerWeek) * swaps;
    const freshPlantProduct = state.plants * pricing.freshPlantPerWeek * weeks;
    const freshProduct = freshBouquetProduct + freshPlantProduct;

    const freshMaintenance = hoursPerWeek * state.hourly * weeks;
    const freshHandling = bouquetCount * swaps * pricing.swapHandlingFee;
    const freshTotal = freshProduct + freshMaintenance + freshHandling;

    /* ─ Blooms: bouquets recurring, plants one-time ─ */
    const bloomsBouquetProduct =
      (state.xl * pricing.bloomsXLPerMonth + state.m * pricing.bloomsMPerMonth) * months;
    const bloomsPlantInvestment = state.plants * pricing.bloomsPlantOneTime; // eenmalig
    const bloomsTotal = bloomsBouquetProduct; // recurring only, for fair 12w compare

    /* ─ Payback of plant investment ─ */
    const weeklyPlantRunning =
      state.plants *
      (pricing.freshPlantPerWeek + (pricing.minutesPerPlant / 60) * state.hourly);
    const paybackWeeks =
      weeklyPlantRunning > 0 ? Math.ceil(bloomsPlantInvestment / weeklyPlantRunning) : 0;

    /* ─ Sustainability impact ─ based on fresh bouquets that are NOT bought ─ */
    const bouquetsAvoided = bouquetCount * weeks;
    const co2Avoided   = bouquetsAvoided * pricing.co2PerBouquet;
    const waterSaved   = bouquetsAvoided * pricing.waterPerBouquet;
    const wasteAvoided = bouquetsAvoided * pricing.wastePerBouquet;

    return {
      weeks,
      savings: Math.max(0, freshTotal - bloomsTotal),
      savedFlowers: Math.max(0, freshProduct - bloomsBouquetProduct),
      savedMaintenance: Math.max(0, freshMaintenance),
      savedHandling: Math.max(0, freshHandling),
      plantInvestment: bloomsPlantInvestment,
      paybackWeeks,
      hasPlants: state.plants > 0,
      hasBouquets: bouquetCount > 0,
      co2Avoided,
      waterSaved,
      wasteAvoided,
      fresh: {
        product: freshProduct,
        maintenance: freshMaintenance,
        handling: freshHandling,
        weekly: freshTotal / weeks,
        total: freshTotal,
      },
      blooms: {
        product: bloomsBouquetProduct,
        plantInvestment: bloomsPlantInvestment,
        maintenance: 0,
        handling: 0,
        weekly: bloomsTotal / weeks,
        total: bloomsTotal,
      },
    };
  }

  /* ── Render ── */
  function render(root, state, config, pricing, ctaCfg) {
    const result = calculate(state, pricing);

    root.innerHTML = `
      <aside class="bsc-inputs">
        <div class="bsc-field">
          <label class="bsc-label">
            <span class="bsc-label-text">Wat voor type bedrijf heb je?</span>
          </label>
          <select class="bsc-select" data-field="company">
            ${Object.keys(config).map(key => `
              <option value="${key}"${state.company === key ? ' selected' : ''}>
                ${COMPANY_LABELS[key] || key}
              </option>
            `).join('')}
          </select>
        </div>

        ${sliderField('xl',     'XL boeketten', 'verse bloemen nu', state.xl)}
        ${sliderField('m',      'M boeketten',  'verse bloemen nu', state.m)}

        <div class="bsc-field">
          <div class="bsc-label">
            <span>
              <span class="bsc-label-text">Verversingsfrequentie</span><br>
              <span class="bsc-label-sub">hoe vaak verse boeketten vervangen worden</span>
            </span>
          </div>
          <div class="bsc-segment" role="radiogroup" aria-label="Verversingsfrequentie">
            ${Object.keys(FREQ).map(key => `
              <button type="button"
                      class="bsc-segment__btn${state.freq === key ? ' bsc-segment__btn--active' : ''}"
                      data-field="freq" data-value="${key}"
                      role="radio" aria-checked="${state.freq === key}">
                ${FREQ[key].label}
              </button>
            `).join('')}
          </div>
        </div>

        ${sliderField('plants', 'Groene planten', 'losse potten / interieur', state.plants)}
        ${sliderField('hourly', 'Externe onderhoudskosten', '€ per uur (bloemist / extern)', state.hourly, '€')}
      </aside>

      <aside class="bsc-results">
        <div class="bsc-results__block">
          <p class="bsc-headline-label">Besparing per seizoen · ${result.weeks} weken</p>
          <h3 class="bsc-headline">
            <span class="bsc-headline__currency">€</span>
            <span class="bsc-num-anim" data-num="savings">0</span>
          </h3>
          <p class="bsc-headline-sub">Totaal over ${result.weeks} weken — ${(FREQ[state.freq] || FREQ.weekly).label.toLowerCase()} verse bloemen vervangen.</p>
        </div>

        <div class="bsc-results__block">
          <div class="bsc-breakdown-item">
            <span class="bsc-breakdown-amount"><span class="bsc-num-anim" data-num="savedFlowers">€0</span></span>
            <span class="bsc-breakdown-label">bespaard op <strong>bloemen &amp; planten</strong></span>
          </div>
          <div class="bsc-breakdown-item">
            <span class="bsc-breakdown-amount"><span class="bsc-num-anim" data-num="savedMaintenance">€0</span></span>
            <span class="bsc-breakdown-label">bespaard op <strong>onderhoud</strong> (tijd × uurtarief)</span>
          </div>
          <div class="bsc-breakdown-item">
            <span class="bsc-breakdown-amount"><span class="bsc-num-anim" data-num="savedHandling">€0</span></span>
            <span class="bsc-breakdown-label">bespaard op <strong>vervanging &amp; handling</strong></span>
          </div>
        </div>

        ${result.hasPlants ? `
        <div class="bsc-results__block bsc-plant-block">
          <p class="bsc-headline-label">Kunstplanten · eenmalige investering</p>
          <div class="bsc-breakdown-item">
            <span class="bsc-breakdown-amount"><span class="bsc-num-anim" data-num="plantInvestment">€0</span></span>
            <span class="bsc-breakdown-label">
              eenmalige aanschaf ${state.plants} kunstplant${state.plants === 1 ? '' : 'en'} —
              terugverdiend in <strong><span class="bsc-num-anim" data-num="paybackWeeks">0</span> weken</strong>
            </span>
          </div>
        </div>
        ` : ''}

        ${result.hasBouquets ? `
        <div class="bsc-results__block bsc-impact-block">
          <p class="bsc-headline-label">Impact · per ${result.weeks} weken</p>
          <div class="bsc-impact-grid">
            <div class="bsc-impact-item">
              <div class="bsc-impact-value"><span class="bsc-num-anim" data-num="co2Avoided">0</span> <span class="bsc-impact-unit">kg</span></div>
              <div class="bsc-impact-label">CO₂ vermeden<br><span class="bsc-impact-sub">geen import &amp; koeltransport</span></div>
            </div>
            <div class="bsc-impact-item">
              <div class="bsc-impact-value"><span class="bsc-num-anim" data-num="waterSaved">0</span> <span class="bsc-impact-unit">L</span></div>
              <div class="bsc-impact-label">Water bespaard<br><span class="bsc-impact-sub">geen waterintensieve kweek</span></div>
            </div>
            <div class="bsc-impact-item">
              <div class="bsc-impact-value"><span class="bsc-num-anim" data-num="wasteAvoided">0</span> <span class="bsc-impact-unit">kg</span></div>
              <div class="bsc-impact-label">Groenafval vermeden<br><span class="bsc-impact-sub">geen wekelijkse GFT</span></div>
            </div>
          </div>
          ${ctaCfg.impactSource ? `<p class="bsc-impact-source">${ctaCfg.impactSource}</p>` : ''}
        </div>
        ` : ''}

        <div class="bsc-results__block">
          <div class="bsc-compare">
            <div class="bsc-card">
              <h4 class="bsc-card__title">Verse bloemen &amp; planten</h4>
              <div class="bsc-card__row"><span>Product</span><span><span class="bsc-num-anim" data-num="freshProduct">€0</span></span></div>
              <div class="bsc-card__row"><span>Onderhoud</span><span><span class="bsc-num-anim" data-num="freshMaintenance">€0</span></span></div>
              <div class="bsc-card__row"><span>Handling</span><span><span class="bsc-num-anim" data-num="freshHandling">€0</span></span></div>
              <div class="bsc-card__total">
                <span class="bsc-card__total-label">${result.weeks} weken</span>
                <span class="bsc-card__total-value"><span class="bsc-num-anim" data-num="freshTotal">€0</span></span>
              </div>
            </div>
            <div class="bsc-card bsc-card--blooms">
              <h4 class="bsc-card__title">Blooms</h4>
              <div class="bsc-card__row"><span>Boeket abonnement</span><span><span class="bsc-num-anim" data-num="bloomsProduct">€0</span></span></div>
              ${result.hasPlants ? `<div class="bsc-card__row"><span>Planten <em>(eenmalig)</em></span><span><span class="bsc-num-anim" data-num="bloomsPlantInvest">€0</span></span></div>` : ''}
              <div class="bsc-card__row"><span>Onderhoud</span><span>inbegrepen</span></div>
              <div class="bsc-card__row"><span>Handling</span><span>inbegrepen</span></div>
              <div class="bsc-card__total">
                <span class="bsc-card__total-label">${result.weeks} weken recurring</span>
                <span class="bsc-card__total-value"><span class="bsc-num-anim" data-num="bloomsTotal">€0</span></span>
              </div>
            </div>
          </div>
        </div>

        <div class="bsc-results__block bsc-lead" data-lead>
          <p class="bsc-lead__title">Ontvang je besparingsoverzicht</p>
          <p class="bsc-lead__sub">We mailen je deze berekening, zodat je hem kunt bewaren of doorsturen naar je manager.</p>
          <form class="bsc-lead__form" data-lead-form novalidate>
            <input class="bsc-lead__input" type="email" name="email" autocomplete="email" required placeholder="E-mailadres">
            <input class="bsc-lead__input" type="text" name="company" autocomplete="organization" placeholder="Bedrijfsnaam (optioneel)">
            <button class="bsc-lead__btn" type="submit">Stuur mijn overzicht</button>
          </form>
          <p class="bsc-lead__msg" data-lead-msg hidden></p>
          <p class="bsc-lead__legal">Geen spam. Je kunt je op elk moment afmelden.</p>
        </div>

        <div class="bsc-results__block">
          <div class="bsc-cta-wrap">
            <a class="bsc-cta bsc-cta--primary" href="${ctaCfg.primaryUrl || '#'}">${ctaCfg.primaryLabel || 'Plan een demo'}</a>
            <a class="bsc-cta bsc-cta--secondary" href="${ctaCfg.secondaryUrl || '#'}">${ctaCfg.secondaryLabel || 'Bekijk abonnementen'}</a>
          </div>
        </div>
      </aside>
    `;

    /* Initial number paint (without animation so first render is stable) */
    updateNumbers(root, result, /* animate */ false);
    updateSliderProgress(root, state);
  }

  function sliderField(field, labelText, subText, value, prefix = '') {
    const lim = LIMITS[field];
    return `
      <div class="bsc-field">
        <div class="bsc-label">
          <span>
            <span class="bsc-label-text">${labelText}</span>
            ${subText ? `<br><span class="bsc-label-sub">${subText}</span>` : ''}
          </span>
          <input class="bsc-value-pill" type="number" inputmode="numeric"
                 data-field="${field}-num"
                 min="${lim.min}" max="${lim.max}" step="${lim.step}"
                 value="${prefix}${value}">
        </div>
        <div class="bsc-slider-row">
          <input class="bsc-slider" type="range"
                 data-field="${field}"
                 min="${lim.min}" max="${lim.max}" step="${lim.step}"
                 value="${value}">
          <div class="bsc-slider-scale">
            <span>${prefix}${lim.min}</span>
            <span>${prefix}${lim.max}</span>
          </div>
        </div>
      </div>
    `;
  }

  function updateSliderProgress(root, state) {
    root.querySelectorAll('.bsc-slider').forEach(sl => {
      const field = sl.dataset.field;
      const lim = LIMITS[field];
      const pct = ((state[field] - lim.min) / (lim.max - lim.min)) * 100;
      sl.style.setProperty('--bsc-progress', pct + '%');
    });
  }

  function updateNumbers(root, result, animate) {
    const map = {
      savings:           result.savings,
      savedFlowers:      result.savedFlowers,
      savedMaintenance:  result.savedMaintenance,
      savedHandling:     result.savedHandling,
      plantInvestment:   result.plantInvestment,
      freshProduct:      result.fresh.product,
      freshMaintenance:  result.fresh.maintenance,
      freshHandling:     result.fresh.handling,
      freshTotal:        result.fresh.total,
      bloomsProduct:     result.blooms.product,
      bloomsPlantInvest: result.blooms.plantInvestment,
      bloomsTotal:       result.blooms.total,
    };

    Object.keys(map).forEach(key => {
      const el = root.querySelector('[data-num="' + key + '"]');
      if (!el) return;
      const val = map[key];
      if (key === 'savings') {
        if (animate) animateNumber(el, val, (n) => Math.round(n).toLocaleString('nl-NL'));
        else el.textContent = Math.round(val).toLocaleString('nl-NL');
      } else {
        if (animate) animateNumber(el, val, (n) => fmtEUR(n));
        else el.textContent = fmtEUR(val);
      }
    });

    /* Plain-number fields (no euro prefix) */
    const plainMap = {
      paybackWeeks: result.paybackWeeks,
      co2Avoided:   result.co2Avoided,
      waterSaved:   result.waterSaved,
      wasteAvoided: result.wasteAvoided,
    };
    Object.keys(plainMap).forEach(key => {
      const el = root.querySelector('[data-num="' + key + '"]');
      if (!el) return;
      const val = plainMap[key];
      const fmt = (n) => Math.round(n).toLocaleString('nl-NL');
      if (animate) animateNumber(el, val, fmt);
      else el.textContent = fmt(val);
    });
  }

  /* ── Init ── */
  function init() {
    const root = document.getElementById(ROOT_ID);
    if (!root) return;

    const config  = JSON.parse(root.dataset.config);
    const pricing = JSON.parse(root.dataset.pricing);
    const ctaCfg = {
      primaryLabel:   root.dataset.ctaPrimaryLabel,
      primaryUrl:     root.dataset.ctaPrimaryUrl,
      secondaryLabel: root.dataset.ctaSecondaryLabel,
      secondaryUrl:   root.dataset.ctaSecondaryUrl,
      impactSource:   root.dataset.impactSource || '',
    };

    /* State: starts with horeca defaults */
    const initial = 'horeca';
    const state = {
      company: initial,
      xl:     config[initial].xl,
      m:      config[initial].m,
      plants: config[initial].plants,
      hourly: config[initial].hourly,
      freq:   config[initial].freq || 'weekly',
    };

    render(root, state, config, pricing, ctaCfg);

    /* Event delegation */
    root.addEventListener('input', (e) => {
      const field = e.target.dataset.field;
      if (!field) return;

      if (field === 'company') {
        const key = e.target.value;
        state.company = key;
        state.xl = config[key].xl;
        state.m  = config[key].m;
        state.plants = config[key].plants;
        state.hourly = config[key].hourly;
        state.freq   = config[key].freq || 'weekly';
        render(root, state, config, pricing, ctaCfg);
        /* Animate numbers on company change */
        const result = calculate(state, pricing);
        updateNumbers(root, result, true);
        return;
      }

      const isNum = field.endsWith('-num');
      const base  = isNum ? field.replace('-num', '') : field;
      if (!(base in LIMITS)) return;

      let raw = e.target.value;
      if (typeof raw === 'string') raw = raw.replace(/[^\d]/g, '');
      let val = parseInt(raw, 10);
      if (Number.isNaN(val)) val = LIMITS[base].min;
      val = Math.max(LIMITS[base].min, Math.min(LIMITS[base].max, val));
      state[base] = val;

      /* Sync sibling input (slider ↔ number) without full rerender */
      const sliderEl = root.querySelector('[data-field="' + base + '"]');
      const numEl    = root.querySelector('[data-field="' + base + '-num"]');
      if (sliderEl && sliderEl !== e.target) sliderEl.value = val;
      if (numEl && numEl !== e.target) {
        const prefix = base === 'hourly' ? '€' : '';
        numEl.value = prefix + val;
      }

      const result = calculate(state, pricing);
      updateNumbers(root, result, true);
      updateSliderProgress(root, state);
    });

    /* Segmented control clicks — frequency */
    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-field="freq"]');
      if (!btn) return;
      const val = btn.dataset.value;
      if (!FREQ[val] || state.freq === val) return;
      state.freq = val;
      /* Toggle active class without full rerender */
      root.querySelectorAll('[data-field="freq"]').forEach(b => {
        const isActive = b.dataset.value === val;
        b.classList.toggle('bsc-segment__btn--active', isActive);
        b.setAttribute('aria-checked', String(isActive));
      });
      /* Also update sub-headline text since it mentions frequency */
      const sub = root.querySelector('.bsc-headline-sub');
      if (sub) sub.textContent =
        'Totaal over ' + pricing.weeks + ' weken — ' +
        FREQ[val].label.toLowerCase() + ' verse bloemen vervangen.';
      const result = calculate(state, pricing);
      updateNumbers(root, result, true);
    });

    /* ── Leadformulier: besparingsoverzicht per e-mail ──
       Stuurt het profiel + de zojuist berekende cijfers naar Klaviyo,
       lijst "Zakelijke leads". Draait binnen init() zodat state/pricing
       altijd de actuele waarden zijn.                                  */
    const KLAVIYO_COMPANY_ID = 'YqmbNL';
    const KLAVIYO_LIST_ID    = 'RQng3J';
    const COMPANY_LABELS = {
      horeca: 'Horeca', kantoor_klein: 'Klein kantoor', kantoor_groot: 'Groot kantoor',
      hotel: 'Hotel', hospitality: 'Hospitality', winkel: 'Winkel'
    };

    root.addEventListener('submit', (e) => {
      const form = e.target.closest('[data-lead-form]');
      if (!form) return;
      e.preventDefault();

      const msg   = root.querySelector('[data-lead-msg]');
      const btn   = form.querySelector('button[type="submit"]');
      const email = (form.elements.email.value || '').trim();
      const org   = (form.elements.company.value || '').trim();

      const say = (text, ok) => {
        if (!msg) return;
        msg.textContent = text;
        msg.hidden = false;
        msg.classList.toggle('bsc-lead__msg--error', !ok);
      };

      if (!email || email.indexOf('@') < 1) { say('Vul een geldig e-mailadres in.', false); return; }

      const r = calculate(state, pricing);
      if (btn) { btn.disabled = true; btn.textContent = 'Versturen…'; }

      const profile = {
        email: email,
        properties: {
          'Bron': 'Besparingscalculator',
          'Type ruimte': COMPANY_LABELS[state.company] || state.company,
          'Besparing 12 weken': Math.round(r.savings),
          'Kosten verse bloemen 12 weken': Math.round(r.fresh.total),
          'Kosten Blooms 12 weken': Math.round(r.blooms.total),
          'XL boeketten': state.xl,
          'M boeketten': state.m,
          'Planten': state.plants,
          'Wisselfrequentie': state.freq,
          'Uurtarief onderhoud': state.hourly
        }
      };
      if (org) profile.organization = org;

      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_COMPANY_ID, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'B2B besparingscalculator',
              profile: { data: { type: 'profile', attributes: profile } }
            },
            relationships: { list: { data: { type: 'list', id: KLAVIYO_LIST_ID } } }
          }
        })
      })
      .then((res) => {
        if (!res.ok && res.status !== 202) throw new Error('subscribe failed');
        form.hidden = true;
        say('Gelukt — je overzicht is onderweg naar ' + email + '.', true);
        if (window.klaviyo) window.klaviyo.push(['identify', { email: email }]);
      })
      .catch(() => {
        say('Er ging iets mis. Probeer het opnieuw of mail ons op hello@blooms.nl.', false);
        if (btn) { btn.disabled = false; btn.textContent = 'Stuur mijn overzicht'; }
      });
    });

    /* Blur number inputs ensures clamped value is shown */
    root.addEventListener('blur', (e) => {
      if (!e.target.dataset || !e.target.dataset.field) return;
      if (!e.target.dataset.field.endsWith('-num')) return;
      const base = e.target.dataset.field.replace('-num', '');
      const prefix = base === 'hourly' ? '€' : '';
      e.target.value = prefix + state[base];
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();


